import {DirectionEnum} from '../enums/direction.enum';
import {UploadedFile} from '@interfaces/file.interface';

import {ModelsInterfaces} from './models.namespace';

export namespace EquipmentInterfaces {
    /**
     * Модель оборудования из справочника
     */
    export interface Model {
        // поля как в БД
        id: string|number;
        title: string;                                                       // Наименование
        application: string;                                                 // Назначение
        country: string;                                                     // Страна производитель
        description: string;                                                 // Описание
        files: UploadedFile[];    // Прикрепленные файлы
        images: UploadedFile[];   // Изображения
        is_clone: boolean;                                                   // Является клоном?
        is_moderated: boolean;                                               // Промодерировано?
        manufacturer: string;                                                // Производитель
        original_id: string|number;                                          // Ссылка на оригинальное оборудование
        regulatoryFields: RegulatoryFields[];                                // Область аттестации
        specifications: {apparatus_group_id: number, attributeIds: any[]}[]; // Спецификация (группа и атрибуты)
        type: EquipmentEnums.Type;                                          // Тип оборудования
        unit_measure_id: number;                                            // Единица измерения
        verification_interval: string|number;                                // Межповерочный интервал
        verification_status: EquipmentEnums.VerificationStatus;             // id Статуса поверки (как именно поверяется)

        // Вычисляемые поля, чтобы на фронте работать уже с готовыми значениями
        type_title?: string;                 // Наименование типа словами
        unit_measure_title?: string;         // Полное наименование единицы измерения
        unit_measure_short_title?: string;   // Короткое наименование единицы измерения
        methods?: ModelsInterfaces.Method[]; // Коллекция методов, полученных из regulatoryFields. Методы полностью объектами, не только id
        groups?: ModelsInterfaces.Group[];   // Группы для оборудования, полученные из specifications
        verification_status_title?: string;  // Наименование статуса поверки
    }

    export interface RegulatoryFields {
        method_id: number|string;
        objectIds: {id: number}[];
    }
}

export namespace EquipmentEnums {
    /**
     * Статусы поверки оборудования
     * common/models/Apparatus.php:78
     */
    export enum VerificationStatus {
        ongoing = 1,        // Поверяется
        calibrating = 2,    // Калибруется
        inapplicable = 3    // Не требуется
    }

    /**
     * Типы оборудования
     */
    export enum Type {
        general = 1,    // Основное
        reference = 2,  // Образец, эталон
        material = 3,   // Материал
        dosimetric = 4, // Дозиметрическое
        device = 5,     // Датчик/Приспособление
        help = 6        // Вспомогательное
    }
}


export namespace EquipmentCardInterfaces {

    /**
     * Все для карточки оборудования
     */
    export namespace Card {
        /**
         * Собственно, карточка оборудования - данные модели из БД
         */
        export interface Item {
            apparatus_id: number;               // id модели
            id: number;
            apparatus: EquipmentInterfaces.Model;

            serial: string;                     // Заводской номер
            without_serial: boolean;            // Без заводского номера
            status: number;                     // Статус (его id), списано, в работе и т.п.
            count: number|string;               // Количество на складе
            year: number|string;
            inventory_number: string;           // Инвентарный номер
            commissioning_date: string;         // Дата ввода в эксплуатацию
            is_leased: number;                  // Является арендованным? 0 или 1
            lease_contract_number: string;      // Номер договора аренды, если арендовано
            rent_expiry_date: string;           // Дата окончания договора (если арендовано)
            owner: string;                      // Владелец (если арендовано)
            documents: UploadedFile[];          // Документы, прикрепленные к карточке

            calibrations: Calibration[];        // Поверки отсортированы по убыванию даты окончания
        }

        /**
         * Поверка оборудования
         */
        export interface Calibration {
            date_begin: string;
            date_end: string;
            id: number;
            number: string;
            organization: string;
            files: UploadedFile[];
        }

        /**
         * Данные карточки с дополнительными вычисляемыми параметрами для просмотра
         */
        export interface ItemView extends Item {
            status_title: string;       // Статус словом, например "В работе",
            is_calibrated: boolean;
            calibration: LastCalibration;
            has_future_calibration: boolean; // Имеется ли еще не наступившая поверка
            /**
             * Информация по объектам, в которых задействовано оборудование
             */
            working_objects?: WorkingObjectInfo[];
            repairs: EquipmentRepairViewData[];
        }

        /**
         * Карточка оборудования для списка на складе
         */
        export interface ListItem {
            id: number;
            title: string;
            /**
             * Количество для отображения, включая единицы измерения
             * Например:
             * * 1 шт
             * * 2 ед
             * * 10 кг
             */
            count_title: string;
            /**
             * Перечисление всех методов (краткие наименования) для оборудования
             * и их полных наименований в качестве тултипа
             * Например, возможны такие значения как:
             * * РГ
             * * РК, УК, ВИК
             */
            methods_info: {
                short_title: string;
                tooltip: string;
            };
            year: number|string;
            serial: string;
            /**
             * Значение актуальной поверки с самой поздней датой окончания
             */
            last_calibration: LastCalibration;

            /**
             * Статус поверки модели оборудования (поверяется, калибруется или поверка не требуется)
             */
            calibration_status: EquipmentEnums.VerificationStatus;
            status: EquipmentCardEnums.EquipmentStatus;
        }

        /**
         * Данные крайней поверки для карточки оборудования.
         */
        export interface LastCalibration {
            status: EquipmentCardEnums.CalibrationExpiredStatus;
            date_begin: string;
            number: string;
            date_end: string;
            files: UploadedFile[];
        }

        /**
         * Информация об объектах, на которых задействовано оборудование
         */
        export interface WorkingObjectInfo {
            id: number;
            date_end: string;
            location_name: string;
        }
    }

    /**
     * Сведения о ремонте
     */
    export interface EquipmentRepairViewData {
        id: number;
        date_begin: string;
        date_end: string;
        description: string;
        files: UploadedFile[];
        place: string;
        is_ignored: boolean;
    }

    /**
     * Группа оборудования
     */
    export interface Group {
        id: number;
        title: string;
    }
}

export namespace EquipmentCardEnums {
    /**
     * Статус окончания поверки оборудования
     * frontend/helpers/CalibrationDateHelper.php
     */
    export enum CalibrationExpiredStatus {
        expired = 1,    // Просрочена
        close = 2,      // Окончание поверки близко (менее 1 месяца)
        soon = 3,       // Окончание поверки скоро (менее 3 месяцев)
        long = 4,       // До окончания поверки еще долго (более 3 месяцев)
    }

    export enum EquipmentStatus {
        working = 1,        // В работе, справно
        conservation = 2,   // Консервация
        repair = 3,         // На ремонте
        dismiss = 4,        // Списано
    }

    /**
     * Значения параметров сортировки
     */
    export enum CardSortingValues {
        title = 'title',
        count = 'count',
        year = 'year',
        serial = 'serial',
    }
}
